import { camelToDashCase } from "@vattenfall/util";

import { logError } from "../functions";

function sanitizeHtml(element: Element | null) {
  return (
    (element?.textContent ?? "")
      // Remove tabs
      .replace(/\t/g, "")
      // Merge multiple spaces into one
      .replace(/\s{2,}/g, " ")
      .trim()
  );
}

/**
 * Decorator that finds an Vattenfall Element projected node by its attribute name and return its text content.
 *
 * Used for resolving projected content into component class properties without using `@ContentChild` or `@ContentChildren`, however with the following limitations:
 *
 * * If multiple instances of the same custom element exist, the slotted values must be the same for all of them.
 * * Projected nodes that are modified at runtime will not be detected.
 * * Will not work with custom elements created at runtime.
 *
 * You can supply an optional name to use in templates when the component is instantiated, that maps to the name of the bound property.
 * By default, the original name of the bound property is used for input binding.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export function Slot(bindingPropertyName?: string) {
  const slottableElementsMap = new Map<string, string>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function (target: any, propertyKey: string) {
    // Use name of class property key if no binding property name has been specified
    bindingPropertyName = bindingPropertyName ?? propertyKey;
    const projectedElement =
      document.body.querySelector(`[${bindingPropertyName}]`) ??
      document.body.querySelector(`[${camelToDashCase(bindingPropertyName)}]`);
    const santizedInput = sanitizeHtml(projectedElement);
    slottableElementsMap.set(bindingPropertyName, santizedInput);
    function getter() {
      return slottableElementsMap.get(bindingPropertyName as string);
    }
    function setter(value: unknown) {
      if (!slottableElementsMap.get(bindingPropertyName as string)) {
        if (value instanceof Element) value = sanitizeHtml(value);
        if (typeof value === "string") slottableElementsMap.set(bindingPropertyName as string, value);
      }
    }
    Object.defineProperty(target, propertyKey, {
      get: getter,
      set: setter,
    });
    if (!target[propertyKey]) logError(`${target.constructor.name}: The slot '${String(propertyKey)}' is required but empty.`);
  };
}
